import { useEffect, useMemo, useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateTrialPersonnelRequestSchema,
  type CreateTrialPersonnelRequest,
  type PersonnelDetailsDto,
  type TrialPersonnelDto,
  type TrialRoleDto,
  type UpdateTrialPersonnelRequest,
  UpdateTrialPersonnelRequestSchema,
} from '@ctms/contracts';
import { isHTTPError } from 'ky';
import { AlertCircle, TriangleAlert, X } from 'lucide-react';
import { z } from 'zod';

import { createAppColumnHelper, useAppTable } from '../../../components/data-table/table-hook.js';
import { getApiErrorMessage } from '../../../api/client.js';
import { trialsApi } from '../trials.api.js';
import { PERSONNEL_CARD_LIST_QUERY, trialQueryKeys } from '../trials.query-keys.js';
import { AddRoleDialog, assignedCatalogRoleCodes } from '../role-catalog/role-catalog.js';
import { ContactCardDialog } from '../../personnel/contact-card-dialog/contact-card-dialog.js';

export interface TeamCatalogProps {
  trialId: number;
  /** Trial SIV (initiation) date, ISO yyyy-mm-dd, or null when not set. Selects whether the assign dialog offers the SIV shortcut. */
  sivDate: string | null;
  /** personnel:write, passed from the cockpit. Independent of trials:write. */
  canWritePersonnel: boolean;
}

const fieldClass =
  'mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none disabled:opacity-50';

const labelClass = 'block text-xs font-medium uppercase tracking-wider text-slate-300';

function openDatePicker(event: { preventDefault: () => void; currentTarget: HTMLInputElement }): void {
  event.preventDefault();
  const input = event.currentTarget;
  if (input.disabled || typeof input.showPicker !== 'function') {
    return;
  }
  try {
    input.showPicker();
  } catch {
    input.focus();
  }
}

function blockDateTyping(event: { key: string; preventDefault: () => void }): void {
  if (event.key !== 'Tab' && event.key !== 'Escape') {
    event.preventDefault();
  }
}

export function personLabel(
  card: Pick<PersonnelDetailsDto, 'firstName' | 'lastName'> | undefined,
): string {
  if (!card) {
    return '—';
  }
  const parts = [card.firstName, card.lastName].filter(
    (part): part is string => part !== null && part.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(' ') : '—';
}

/**
 * Picker variant of `personLabel` — last name first ("NAZWISKO IMIĘ"). The
 * client asked for the reversed order only in the lists of choice (the
 * "Członek zespołu" select of the assign dialog, `personnelLabel` in
 * `personnel-select`); the team table and the name warnings keep the
 * first-name-first order (mail 5 / B.2; D.3 settled 15.10 — IMIĘ NAZWISKO stays in the table).
 */
export function personPickerLabel(
  card: Pick<PersonnelDetailsDto, 'firstName' | 'lastName'> | undefined,
): string {
  if (!card) {
    return '—';
  }
  const parts = [card.lastName, card.firstName].filter(
    (part): part is string => part !== null && part.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(' ') : '—';
}

export function roleName(role: Pick<TrialRoleDto, 'name'> | undefined): string {
  const name = role?.name.trim() ?? '';
  return name.length > 0 ? name : '—';
}

/** Principal investigator is the Investigator role plus the flag. Any other role stays unmarked. */
export function principalInvestigatorLabel(
  pi: boolean,
  roleCode: TrialRoleDto['roleCode'] | undefined,
): 'Tak' | '—' {
  return pi && roleCode === 'INV' ? 'Tak' : '—';
}

/**
 * Activity is active + the dates. An inactive row, or one that already has an end date, stays in
 * the table and is marked as outside the visit-registration pick list (REQ-TRIAL-018); that pick
 * list is not rendered here. Wording follows the client (menu „Zespół badania"): the column reads
 * `Aktywność` and an inactive person reads `Nieaktywny od RRRR-MM-DD` (the end-of-participation
 * date), or just `Nieaktywny` when there is no end date.
 */
export function presenceLabel(
  row: Pick<TrialPersonnelDto, 'active' | 'dateStart' | 'dateEnd'>,
): string {
  if (!row.active || row.dateEnd !== null) {
    return row.dateEnd ? `Nieaktywny od ${row.dateEnd}` : 'Nieaktywny';
  }
  return row.dateStart ? `Aktywny od ${row.dateStart}` : 'Aktywny';
}

export interface TeamTableRow {
  id: number;
  person: string;
  role: string;
  principal: 'Tak' | '—';
  presence: string;
  actions: string;
  assignment: TrialPersonnelDto;
}

/**
 * Default personnel ordering: lastName → firstName (Polish collation) → id.
 * The backend already returns cards sorted this way (PersonnelService.listPersonnel
 * is the single source), but every picker here sorts defensively so the team
 * panel and the assign dialog stay stable regardless of API order
 * (bug local-20261009-sortowanie-personelu).
 */
export function comparePersonnelByName(
  a: Pick<PersonnelDetailsDto, 'id' | 'firstName' | 'lastName'>,
  b: Pick<PersonnelDetailsDto, 'id' | 'firstName' | 'lastName'>,
): number {
  const byLast = (a.lastName ?? '').localeCompare(b.lastName ?? '', 'pl');
  if (byLast !== 0) {
    return byLast;
  }
  const byFirst = (a.firstName ?? '').localeCompare(b.firstName ?? '', 'pl');
  if (byFirst !== 0) {
    return byFirst;
  }
  return a.id - b.id;
}

/**
 * Team panel ordering (B1 / @req:new#trial-team-order): rows are grouped by role —
 * Investigator → Study Coordinator → Study Nurse, then every other role together.
 * Inside the Investigator group the principal investigator comes first; inside any
 * group the secondary key is the person's name (see compareTeamAssignments).
 */
const TEAM_ROLE_GROUP: Record<string, number> = { INV: 0, SC: 1, SN: 2 };
const OTHER_ROLE_GROUP = 3;

export function teamRoleGroup(roleCode: string | null | undefined): number {
  if (roleCode === null || roleCode === undefined) {
    return OTHER_ROLE_GROUP;
  }
  return TEAM_ROLE_GROUP[roleCode] ?? OTHER_ROLE_GROUP;
}

/**
 * Orders study-team assignments for the panel table: first by role group
 * (teamRoleGroup), then — inside the Investigator group only — the principal
 * investigator leads, and finally by the person's name (Polish collation) with the
 * assignment id as the last tiebreak.
 */
export function compareTeamAssignments(
  a: TrialPersonnelDto,
  b: TrialPersonnelDto,
  cardsById: ReadonlyMap<number, PersonnelDetailsDto>,
  rolesById: ReadonlyMap<number, TrialRoleDto>,
): number {
  const groupA = teamRoleGroup(rolesById.get(a.roleId)?.roleCode);
  const groupB = teamRoleGroup(rolesById.get(b.roleId)?.roleCode);
  if (groupA !== groupB) {
    return groupA - groupB;
  }
  if (groupA === 0 && a.pi !== b.pi) {
    return a.pi ? -1 : 1;
  }
  const cardA = cardsById.get(a.personnelDetailsId);
  const cardB = cardsById.get(b.personnelDetailsId);
  const byPerson = comparePersonnelByName(
    { id: a.personnelDetailsId, firstName: cardA?.firstName ?? '', lastName: cardA?.lastName ?? '' },
    { id: b.personnelDetailsId, firstName: cardB?.firstName ?? '', lastName: cardB?.lastName ?? '' },
  );
  return byPerson !== 0 ? byPerson : a.id - b.id;
}

export function toTeamTableRow(
  assignment: TrialPersonnelDto,
  cardsById: ReadonlyMap<number, PersonnelDetailsDto>,
  rolesById: ReadonlyMap<number, TrialRoleDto>,
): TeamTableRow {
  const card = cardsById.get(assignment.personnelDetailsId);
  const role = rolesById.get(assignment.roleId);
  const comment = assignment.roleComments?.trim() ?? '';
  return {
    id: assignment.id,
    person: personLabel(card),
    role: roleName(role),
    principal: principalInvestigatorLabel(assignment.pi, role?.roleCode),
    presence: presenceLabel(assignment),
    actions: comment.length > 0 ? comment : '—',
    assignment,
  };
}

export const AssignPersonFormSchema = z
  .object({
    personnelDetailsId: z.string().min(1, 'Wybierz członka zespołu'),
    roleId: z.string().min(1, 'Wybierz rolę w badaniu'),
    pi: z.boolean().optional(),
    roleComments: z.string().max(10_000),
    dateStart: z.string(),
    dateEnd: z.string(),
    active: z.boolean(),
  })
  .superRefine((value, ctx) => {
    const message = dateRangeMessage(value.dateStart, value.dateEnd);
    if (message) {
      ctx.addIssue({ code: 'custom', path: ['dateEnd'], message });
    }
  });

export type AssignPersonFormInput = z.infer<typeof AssignPersonFormSchema>;

const EMPTY_ASSIGN_FORM: AssignPersonFormInput = {
  personnelDetailsId: '',
  roleId: '',
  pi: false,
  roleComments: '',
  dateStart: '',
  dateEnd: '',
  active: true,
};

/** Message comes from CreateTrialPersonnelRequestSchema. Empty dates are allowed. */
export function dateRangeMessage(dateStart: string, dateEnd: string): string | null {
  if (dateStart.length === 0 || dateEnd.length === 0) {
    return null;
  }
  const parsed = CreateTrialPersonnelRequestSchema.safeParse({
    trialId: 1,
    roleId: 1,
    personnelDetailsId: 1,
    dateStart,
    dateEnd,
  });
  if (parsed.success) {
    return null;
  }
  const issue = parsed.error.issues.find((item) => item.path[0] === 'dateEnd');
  return issue?.message ?? null;
}

/** Assignment body only. Contact-card PII stays on the personnel record. */
export function toCreateTrialPersonnelRequest(
  trialId: number,
  data: AssignPersonFormInput,
  roleCode: TrialRoleDto['roleCode'] | null,
): CreateTrialPersonnelRequest {
  const request: CreateTrialPersonnelRequest = {
    trialId,
    roleId: Number(data.roleId),
    personnelDetailsId: Number(data.personnelDetailsId),
    pi: roleCode === 'INV' && data.pi === true,
    active: data.active,
  };
  const comments = data.roleComments.trim();
  if (comments.length > 0) {
    request.roleComments = comments;
  }
  if (data.dateStart.length > 0) {
    request.dateStart = data.dateStart;
  }
  if (data.dateEnd.length > 0) {
    request.dateEnd = data.dateEnd;
  }
  return request;
}

export const EditAssignmentFormSchema = z
  .object({
    roleId: z.string().min(1, 'Wybierz rolę w badaniu'),
    pi: z.boolean().optional(),
    roleComments: z.string().max(10_000),
    dateStart: z.string(),
    dateEnd: z.string(),
    active: z.boolean(),
  })
  .superRefine((value, ctx) => {
    const message = dateRangeMessage(value.dateStart, value.dateEnd);
    if (message) {
      ctx.addIssue({ code: 'custom', path: ['dateEnd'], message });
    }
  });

export type EditAssignmentFormInput = z.infer<typeof EditAssignmentFormSchema>;

/**
 * Update body only. Empty comment/dates travel as explicit nulls so an edit can
 * clear them (the create mapper omits empty values instead). The PI flag is kept
 * only for the Investigator role (REQ-TRIAL-017).
 */
export function toUpdateTrialPersonnelRequest(
  data: EditAssignmentFormInput,
  roleCode: TrialRoleDto['roleCode'] | null,
): UpdateTrialPersonnelRequest {
  const comments = data.roleComments.trim();
  return {
    roleId: Number(data.roleId),
    pi: roleCode === 'INV' && data.pi === true,
    roleComments: comments.length > 0 ? comments : null,
    active: data.active,
    dateStart: data.dateStart.length > 0 ? data.dateStart : null,
    dateEnd: data.dateEnd.length > 0 ? data.dateEnd : null,
  };
}

/** Active principal investigators per REQ-TRIAL-029: the Investigator role plus the PI flag, active, no end date. */
export function activePrincipalInvestigators(
  assignments: readonly TrialPersonnelDto[],
  rolesById: ReadonlyMap<number, TrialRoleDto>,
): TrialPersonnelDto[] {
  return assignments.filter(
    (assignment) =>
      assignment.pi &&
      rolesById.get(assignment.roleId)?.roleCode === 'INV' &&
      assignment.active &&
      assignment.dateEnd === null,
  );
}

/**
 * B.4b / REQ-TRIAL-022 (@req:new#person-name-warning): a card with the same first and last
 * name is only a warning — the save still goes through. Both parts are trimmed and compared
 * case-insensitively and must both match. Returns the message naming the existing card, or null
 * when there is no match or the draft is still incomplete.
 */
export function duplicatePersonWarning(
  cards: readonly PersonnelDetailsDto[],
  draft: { firstName: string; lastName: string },
): string | null {
  const firstName = draft.firstName.trim().toLowerCase();
  const lastName = draft.lastName.trim().toLowerCase();
  if (firstName.length === 0 || lastName.length === 0) {
    return null;
  }
  const match = cards.find(
    (card) =>
      (card.firstName ?? '').trim().toLowerCase() === firstName &&
      (card.lastName ?? '').trim().toLowerCase() === lastName,
  );
  if (!match) {
    return null;
  }
  return `W kartotece istnieje już osoba o tym samym imieniu i nazwisku: ${personLabel(match)}. Zapis nie zostanie zablokowany.`;
}

/** Dialog warning text for a second active PI. Null when there is no other active PI. */
export function secondPrincipalInvestigatorWarning(names: readonly string[]): string | null {
  if (names.length === 0) {
    return null;
  }
  if (names.length === 1) {
    return 'W tym badaniu jest już aktywny główny badacz: ' + names[0] + '. Zapis nie zostanie zablokowany.';
  }
  return 'W tym badaniu jest już więcej niż jeden aktywny główny badacz (m.in. ' + names.join(', ') + '). Zapis nie zostanie zablokowany.';
}

const columnHelper = createAppColumnHelper<TeamTableRow>();

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

export function TeamCatalog({ trialId, sivDate, canWritePersonnel }: TeamCatalogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<TrialPersonnelDto | null>(null);
  const [deletingRow, setDeletingRow] = useState<{ assignment: TrialPersonnelDto; person: string; role: string } | null>(null);
  const [deactivatingRow, setDeactivatingRow] = useState<{ assignment: TrialPersonnelDto; person: string; role: string } | null>(null);

  const teamQuery = useQuery({
    queryKey: trialQueryKeys.team(trialId),
    queryFn: () => trialsApi.listTrialPersonnel(trialId),
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });
  const rolesQuery = useQuery({
    queryKey: trialQueryKeys.roles(trialId),
    queryFn: () => trialsApi.listRoles(trialId),
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });
  const cardsQuery = useQuery({
    queryKey: trialQueryKeys.personnelCards(PERSONNEL_CARD_LIST_QUERY),
    queryFn: () => trialsApi.listPersonnel(PERSONNEL_CARD_LIST_QUERY),
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  const roles = rolesQuery.data ?? [];
  const cards = cardsQuery.data?.items ?? [];
  const isLoading = teamQuery.isLoading || rolesQuery.isLoading || cardsQuery.isLoading;
  const isError = teamQuery.isError || rolesQuery.isError || cardsQuery.isError;

  const sortedCards = useMemo(() => [...cards].sort(comparePersonnelByName), [cards]);

  const rows = useMemo(() => {
    const cardsById = new Map(cards.map((card) => [card.id, card]));
    const rolesById = new Map(roles.map((role) => [role.id, role]));
    const assignments = [...(teamQuery.data ?? [])].sort((a, b) =>
      compareTeamAssignments(a, b, cardsById, rolesById),
    );
    return assignments.map((assignment) => toTeamTableRow(assignment, cardsById, rolesById));
  }, [cards, roles, teamQuery.data]);

  const activePis = useMemo(() => {
    const rolesById = new Map(roles.map((role) => [role.id, role]));
    return activePrincipalInvestigators(teamQuery.data ?? [], rolesById);
  }, [roles, teamQuery.data]);

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor('person', { header: 'Osoba' }),
        columnHelper.accessor('role', { header: 'Rola' }),
        columnHelper.accessor('principal', { header: 'Główny badacz' }),
        columnHelper.display({
          id: 'presence',
          header: 'Aktywność',
          cell: (info) => {
            const assignment = info.row.original.assignment;
            if (!assignment.active || assignment.dateEnd !== null) {
              return (
                <span
                  data-testid="team-presence-inactive"
                  className="inline-flex items-center rounded-full border border-amber-800/60 bg-amber-950/40 px-2 py-0.5 text-xs text-amber-300"
                >
                  {presenceLabel(assignment)}
                </span>
              );
            }
            return <span>{presenceLabel(assignment)}</span>;
          },
        }),
        columnHelper.display({
          id: 'actions',
          header: 'Działania',
          cell: (info) => {
            const row = info.row.original;
            return (
              <div className="flex items-center justify-between gap-2">
                <span>{row.actions}</span>
                {canWritePersonnel && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingAssignment(row.assignment)}
                      className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800"
                    >
                      Edytuj
                    </button>
                    {row.assignment.active && (
                      <button
                        type="button"
                        onClick={() =>
                          setDeactivatingRow({ assignment: row.assignment, person: row.person, role: row.role })
                        }
                        className="rounded-md border border-amber-800/60 px-2 py-1 text-xs text-amber-300 hover:bg-slate-800"
                      >
                        Oznacz jako nieaktywną
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        setDeletingRow({ assignment: row.assignment, person: row.person, role: row.role })
                      }
                      className="rounded-md border border-slate-700 px-2 py-1 text-xs text-rose-300 hover:bg-slate-800"
                    >
                      Usuń
                    </button>
                  </div>
                )}
              </div>
            );
          },
        }),
      ]),
    [canWritePersonnel],
  );
  const table = useAppTable({ data: rows, columns });

  const closeDialog = (): void => {
    setDialogOpen(false);
  };

  const handleCreated = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.team(trialId) });
    closeDialog();
  };

  const handleUpdated = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.team(trialId) });
    setEditingAssignment(null);
  };

  const handleDeleted = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.team(trialId) });
    setDeletingRow(null);
  };

  const handleDeactivated = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.team(trialId) });
    setDeactivatingRow(null);
  };

  const retry = (): void => {
    void teamQuery.refetch();
    void rolesQuery.refetch();
    void cardsQuery.refetch();
  };

  return (
    <section aria-label="Zespół badania" className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
      <h3 className="text-sm font-semibold text-slate-100">Zespół badania</h3>
      <p className="mt-1 text-xs text-slate-400">
        Ten sam członek zespołu może wrócić w innym badaniu z inną rolą.
      </p>

      {activePis.length > 1 && (
        <div
          role="status"
          className="mt-3 flex items-center gap-2 rounded-md border border-amber-800/60 bg-amber-950/40 p-3 text-sm text-amber-300"
        >
          <TriangleAlert className="h-4 w-4 shrink-0" />
          <span>W badaniu jest więcej niż jeden aktywny główny badacz.</span>
        </div>
      )}

      {isLoading ? (
        <p className="py-6 text-sm text-slate-400">Ładowanie zespołu badania</p>
      ) : isError ? (
        <div role="alert" className="mt-3 text-sm text-rose-300">
          Nie udało się wczytać zespołu badania.
          <button type="button" onClick={retry} className="ml-3 underline">
            Spróbuj ponownie
          </button>
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-xs text-slate-400">Brak osób w zespole badania</p>
      ) : (
        <div className="mt-3 overflow-hidden rounded-lg border border-slate-800">
          <table className="w-full text-left text-xs">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-slate-800 bg-slate-950/60">
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} scope="col" className="px-3 py-2 text-slate-400">
                      <table.FlexRender header={header} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b border-slate-800/80">
                  {row.getAllCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2 text-slate-200">
                      <table.FlexRender cell={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canWritePersonnel && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500"
          >
            Przypisz osobę
          </button>
        </div>
      )}

      {dialogOpen && (
        <AssignPersonDialog
          trialId={trialId}
          sivDate={sivDate}
          roles={roles}
          cards={sortedCards}
          existingPrincipalInvestigators={activePis}
          existingAssignments={teamQuery.data ?? []}
          onClose={closeDialog}
          onCreated={handleCreated}
        />
      )}

      {editingAssignment !== null && (
        <EditAssignmentDialog
          trialId={trialId}
          assignment={editingAssignment}
          roles={roles}
          cards={sortedCards}
          existingPrincipalInvestigators={activePis}
          onClose={() => setEditingAssignment(null)}
          onSaved={handleUpdated}
        />
      )}

      {deletingRow !== null && (
        <DeleteAssignmentDialog
          trialId={trialId}
          assignment={deletingRow.assignment}
          person={deletingRow.person}
          role={deletingRow.role}
          onClose={() => setDeletingRow(null)}
          onDeleted={handleDeleted}
        />
      )}

      {deactivatingRow !== null && (
        <DeactivateAssignmentDialog
          trialId={trialId}
          assignment={deactivatingRow.assignment}
          person={deactivatingRow.person}
          role={deactivatingRow.role}
          onClose={() => setDeactivatingRow(null)}
          onDeactivated={handleDeactivated}
        />
      )}
    </section>
  );
}

function AssignPersonDialog({
  trialId,
  sivDate,
  roles,
  cards,
  existingPrincipalInvestigators,
  existingAssignments,
  onClose,
  onCreated,
}: {
  trialId: number;
  sivDate: string | null;
  roles: TrialRoleDto[];
  cards: PersonnelDetailsDto[];
  existingPrincipalInvestigators: TrialPersonnelDto[];
  /** Team assignments of this trial — powers the default hiding of inactive people (D.2, mail 6). */
  existingAssignments: TrialPersonnelDto[];
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addRoleOpen, setAddRoleOpen] = useState(false);
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [addedCards, setAddedCards] = useState<PersonnelDetailsDto[]>([]);
  const [pendingSelectId, setPendingSelectId] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [internalOnly, setInternalOnly] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AssignPersonFormInput>({
    resolver: zodResolver(AssignPersonFormSchema),
    defaultValues: EMPTY_ASSIGN_FORM,
  });
  const roleId = watch('roleId');
  const piAllowed = roles.some((role) => String(role.id) === roleId && role.roleCode === 'INV');
  const piChecked = watch('pi');
  const otherActivePiNames = useMemo(
    () =>
      existingPrincipalInvestigators.map((assignment) =>
        personLabel(cards.find((card) => card.id === assignment.personnelDetailsId)),
      ),
    [existingPrincipalInvestigators, cards],
  );
  const piWarning =
    piAllowed && piChecked === true ? secondPrincipalInvestigatorWarning(otherActivePiNames) : null;

  // D.2 (mail 6): a person whose assignment in THIS trial is inactive is hidden from the
  // picker by default (they ended their participation); the "Pokaż nieaktywne" toggle
  // reveals them for the case they come back. Cards with an ACTIVE assignment stay
  // selectable — duplicate assignments remain a UI warning, not a block.
  const inactiveAssignedPersonnelIds = useMemo(
    () =>
      new Set(
        existingAssignments
          .filter((assignment) => !assignment.active)
          .map((assignment) => assignment.personnelDetailsId),
      ),
    [existingAssignments],
  );

  // The catalogue cards plus any added here, de-duplicated by id (an added card also comes
  // back through the invalidated cards query, so the same id must not appear twice).
  const selectableCards = useMemo(() => {
    const byId = new Map<number, PersonnelDetailsDto>();
    for (const card of cards) {
      byId.set(card.id, card);
    }
    for (const card of addedCards) {
      byId.set(card.id, card);
    }
    return [...byId.values()]
      .filter((card) => showInactive || !inactiveAssignedPersonnelIds.has(card.id))
      .filter((card) => !internalOnly || card.internalPersonnel)
      .sort(comparePersonnelByName);
  }, [cards, addedCards, showInactive, internalOnly, inactiveAssignedPersonnelIds]);

  // The freshly added card must be in the option list before the select can take its id,
  // so select it one render after it was appended.
  useEffect(() => {
    if (pendingSelectId === null) {
      return;
    }
    if (!selectableCards.some((card) => String(card.id) === pendingSelectId)) {
      return;
    }
    setValue('personnelDetailsId', pendingSelectId, { shouldValidate: true });
    setPendingSelectId(null);
  }, [pendingSelectId, selectableCards, setValue]);

  const handleRoleCreated = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.roles(trialId) });
    setAddRoleOpen(false);
  };

  const handlePersonCreated = (created: PersonnelDetailsDto): void => {
    setAddedCards((previous) => [...previous.filter((card) => card.id !== created.id), created]);
    setPendingSelectId(String(created.id));
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.personnelCards(PERSONNEL_CARD_LIST_QUERY) });
    setAddPersonOpen(false);
  };

  const onSubmit = async (data: AssignPersonFormInput): Promise<void> => {
    const dateMessage = dateRangeMessage(data.dateStart, data.dateEnd);
    if (dateMessage) {
      setErrorMessage(dateMessage);
      return;
    }
    const selectedRole = roles.find((role) => String(role.id) === data.roleId);
    const request = toCreateTrialPersonnelRequest(trialId, data, selectedRole?.roleCode ?? null);
    const parsed = CreateTrialPersonnelRequestSchema.safeParse(request);
    if (!parsed.success) {
      const dateIssue = parsed.error.issues.find((issue) => issue.path[0] === 'dateEnd');
      setErrorMessage(dateIssue?.message ?? 'Nie udało się zapisać przypisania.');
      return;
    }
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      await trialsApi.createTrialPersonnel(trialId, parsed.data);
      onCreated();
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się zapisać przypisania.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="assign-person-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2 id="assign-person-title" className="text-base font-semibold text-slate-100">
              Przypisz osobę do zespołu
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              Rolę możesz dodać bezpośrednio tutaj, bez wychodzenia z okna. Flaga głównego badacza jest dostępna tylko przy roli Investigator.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={isSubmitting} aria-label="Zamknij" className="rounded-md p-1.5 text-slate-400">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4" noValidate>
          {errorMessage && (
            <div role="alert" className="flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <label htmlFor="assign-person-card" className={labelClass}>Członek zespołu</label>
            <select id="assign-person-card" disabled={isSubmitting} className={fieldClass} {...register('personnelDetailsId')}>
              <option value="">Wybierz członka zespołu</option>
              {selectableCards.map((card) => (
                <option key={card.id} value={String(card.id)}>
                  {inactiveAssignedPersonnelIds.has(card.id)
                    ? `${personPickerLabel(card)} (osoba nieaktywna w badaniu)`
                    : personPickerLabel(card)}
                </option>
              ))}
            </select>
            {errors.personnelDetailsId && (
              <p className="mt-1 text-xs text-rose-400">{errors.personnelDetailsId.message}</p>
            )}
            <p className="mt-2 text-xs text-slate-400">
              Nie ma osoby na liście? Dodaj ją bez wychodzenia z tego okna. Osoby nieaktywne w tym
              badaniu są domyślnie ukryte.
            </p>
            <label className="mt-2 flex items-center gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                checked={showInactive}
                disabled={isSubmitting}
                onChange={(event) => {
                  setShowInactive(event.target.checked);
                }}
              />
              Pokaż nieaktywne
            </label>
            <label className="mt-2 flex items-center gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                checked={internalOnly}
                disabled={isSubmitting}
                onChange={(event) => {
                  setInternalOnly(event.target.checked);
                }}
              />
              Tylko pracownicy ośrodka
            </label>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => setAddPersonOpen(true)}
              className="mt-1 rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-50"
            >
              Dodaj osobę
            </button>
          </div>

          <div>
            <label htmlFor="assign-person-role" className={labelClass}>Rola w badaniu</label>
            <select
              id="assign-person-role"
              disabled={isSubmitting}
              className={fieldClass}
              {...register('roleId', {
                onChange: (event: { target: { value: string } }) => {
                  const next = roles.find((role) => String(role.id) === event.target.value);
                  if (next?.roleCode !== 'INV') {
                    setValue('pi', false);
                  }
                },
              })}
            >
              <option value="">Wybierz rolę w badaniu</option>
              {roles.map((role) => (
                <option key={role.id} value={String(role.id)}>{role.roleCode !== null ? `🔒 ${role.name}` : role.name}</option>
              ))}
            </select>
            {errors.roleId && <p className="mt-1 text-xs text-rose-400">{errors.roleId.message}</p>}
          </div>

          <div className="rounded-md border border-slate-800 bg-slate-950/40 p-3">
            <p className="text-xs text-slate-400">
              Nie ma roli na liście? Dodaj ją bez wychodzenia z tego okna.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setAddRoleOpen(true)}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-50"
              >
                Dodaj rolę
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input type="checkbox" disabled={!piAllowed || isSubmitting} {...register('pi')} />
            Główny badacz
          </label>

          {piWarning && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-md border border-amber-800/60 bg-amber-950/40 p-3 text-sm text-amber-300"
            >
              <TriangleAlert className="h-4 w-4 shrink-0" />
              <span>{piWarning}</span>
            </div>
          )}

          <div>
            <label htmlFor="assign-person-comments" className={labelClass}>Komentarz (opcjonalnie)</label>
            <input
              id="assign-person-comments"
              type="text"
              disabled={isSubmitting}
              className={fieldClass}
              {...register('roleComments')}
            />
          </div>

          <div>
            <label htmlFor="assign-date-start" className={labelClass}>Data rozpoczęcia udziału</label>
            <input
              id="assign-date-start"
              type="date"
              disabled={isSubmitting}
              className={`${fieldClass} scheme-dark cursor-pointer`}
              {...register('dateStart')}
              onClick={openDatePicker}
              onKeyDown={blockDateTyping}
            />
            {sivDate !== null && (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setValue('dateStart', sivDate)}
                className="mt-1 text-xs text-cyan-400 underline hover:text-cyan-300 disabled:opacity-50"
              >
                weź datę SIV
              </button>
            )}
          </div>

          <div>
            <label htmlFor="assign-date-end" className={labelClass}>Data zakończenia udziału</label>
            <input
              id="assign-date-end"
              type="date"
              disabled={isSubmitting}
              className={`${fieldClass} scheme-dark cursor-pointer`}
              {...register('dateEnd')}
              onClick={openDatePicker}
              onKeyDown={blockDateTyping}
            />
            {errors.dateEnd && <p className="mt-1 text-xs text-rose-400">{errors.dateEnd.message}</p>}
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input type="checkbox" disabled={isSubmitting} {...register('active')} />
            Udział aktywny
          </label>

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300">
              Anuluj
            </button>
            <button type="submit" disabled={isSubmitting} className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50">
              {isSubmitting ? 'Zapisywanie…' : 'Zapisz przypisanie'}
            </button>
          </div>
        </form>
      </div>

      {addRoleOpen && (
        <AddRoleDialog
          trialId={trialId}
          existingRoleCodes={assignedCatalogRoleCodes(roles)}
          onClose={() => setAddRoleOpen(false)}
          onCreated={handleRoleCreated}
        />
      )}
      {addPersonOpen && (
        <ContactCardDialog
          editor={{ mode: 'create' }}
          canWrite={true}
          title="Nowa osoba"
          submitLabel="Zapisz osobę"
          duplicateWarning={(draft) => duplicatePersonWarning(selectableCards, draft)}
          onClose={() => setAddPersonOpen(false)}
          onSaved={handlePersonCreated}
        />
      )}
    </div>
  );
}

function EditAssignmentDialog({
  trialId,
  assignment,
  roles,
  cards,
  existingPrincipalInvestigators,
  onClose,
  onSaved,
}: {
  trialId: number;
  assignment: TrialPersonnelDto;
  roles: TrialRoleDto[];
  cards: PersonnelDetailsDto[];
  existingPrincipalInvestigators: TrialPersonnelDto[];
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EditAssignmentFormInput>({
    resolver: zodResolver(EditAssignmentFormSchema),
    defaultValues: {
      roleId: String(assignment.roleId),
      pi: assignment.pi,
      roleComments: assignment.roleComments ?? '',
      dateStart: assignment.dateStart ?? '',
      dateEnd: assignment.dateEnd ?? '',
      active: assignment.active,
    },
  });
  const roleId = watch('roleId');
  const piAllowed = roles.some((role) => String(role.id) === roleId && role.roleCode === 'INV');
  const piChecked = watch('pi');
  const otherActivePiNames = useMemo(
    () =>
      existingPrincipalInvestigators
        .filter((row) => row.id !== assignment.id)
        .map((row) => personLabel(cards.find((card) => card.id === row.personnelDetailsId))),
    [existingPrincipalInvestigators, cards, assignment.id],
  );
  const piWarning =
    piAllowed && piChecked === true ? secondPrincipalInvestigatorWarning(otherActivePiNames) : null;
  const person = personLabel(cards.find((card) => card.id === assignment.personnelDetailsId));

  const onSubmit = async (data: EditAssignmentFormInput): Promise<void> => {
    const dateMessage = dateRangeMessage(data.dateStart, data.dateEnd);
    if (dateMessage) {
      setErrorMessage(dateMessage);
      return;
    }
    const selectedRole = roles.find((role) => String(role.id) === data.roleId);
    const request = toUpdateTrialPersonnelRequest(data, selectedRole?.roleCode ?? null);
    const parsed = UpdateTrialPersonnelRequestSchema.safeParse(request);
    if (!parsed.success) {
      const dateIssue = parsed.error.issues.find((issue) => issue.path[0] === 'dateEnd');
      setErrorMessage(dateIssue?.message ?? 'Nie udało się zapisać przypisania.');
      return;
    }
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      await trialsApi.updateTrialPersonnel(trialId, assignment.id, parsed.data);
      onSaved();
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się zapisać przypisania.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-assignment-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2 id="edit-assignment-title" className="text-base font-semibold text-slate-100">
              Edytuj przypisanie do zespołu
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              Osoba pozostaje ta sama. Flaga głównego badacza jest dostępna tylko przy roli Investigator.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={isSubmitting} aria-label="Zamknij" className="rounded-md p-1.5 text-slate-400">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4" noValidate>
          {errorMessage && (
            <div role="alert" className="flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <label htmlFor="edit-assignment-person" className={labelClass}>Osoba</label>
            <input id="edit-assignment-person" type="text" disabled className={fieldClass} value={person} readOnly />
          </div>

          <div>
            <label htmlFor="edit-assignment-role" className={labelClass}>Rola w badaniu</label>
            <select
              id="edit-assignment-role"
              disabled={isSubmitting}
              className={fieldClass}
              {...register('roleId', {
                onChange: (event: { target: { value: string } }) => {
                  const next = roles.find((role) => String(role.id) === event.target.value);
                  if (next?.roleCode !== 'INV') {
                    setValue('pi', false);
                  }
                },
              })}
            >
              <option value="">Wybierz rolę w badaniu</option>
              {roles.map((role) => (
                <option key={role.id} value={String(role.id)}>{role.roleCode !== null ? `🔒 ${role.name}` : role.name}</option>
              ))}
            </select>
            {errors.roleId && <p className="mt-1 text-xs text-rose-400">{errors.roleId.message}</p>}
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input type="checkbox" disabled={!piAllowed || isSubmitting} {...register('pi')} />
            Główny badacz
          </label>

          {piWarning && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-md border border-amber-800/60 bg-amber-950/40 p-3 text-sm text-amber-300"
            >
              <TriangleAlert className="h-4 w-4 shrink-0" />
              <span>{piWarning}</span>
            </div>
          )}

          <div>
            <label htmlFor="edit-assignment-comments" className={labelClass}>Komentarz (opcjonalnie)</label>
            <input
              id="edit-assignment-comments"
              type="text"
              disabled={isSubmitting}
              className={fieldClass}
              {...register('roleComments')}
            />
          </div>

          <div>
            <label htmlFor="edit-date-start" className={labelClass}>Data rozpoczęcia udziału</label>
            <input
              id="edit-date-start"
              type="date"
              disabled={isSubmitting}
              className={`${fieldClass} scheme-dark cursor-pointer`}
              {...register('dateStart')}
              onClick={openDatePicker}
              onKeyDown={blockDateTyping}
            />
          </div>

          <div>
            <label htmlFor="edit-date-end" className={labelClass}>Data zakończenia udziału</label>
            <input
              id="edit-date-end"
              type="date"
              disabled={isSubmitting}
              className={`${fieldClass} scheme-dark cursor-pointer`}
              {...register('dateEnd')}
              onClick={openDatePicker}
              onKeyDown={blockDateTyping}
            />
            {errors.dateEnd && <p className="mt-1 text-xs text-rose-400">{errors.dateEnd.message}</p>}
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input type="checkbox" disabled={isSubmitting} {...register('active')} />
            Udział aktywny
          </label>

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300">
              Anuluj
            </button>
            <button type="submit" disabled={isSubmitting} className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50">
              {isSubmitting ? 'Zapisywanie…' : 'Zapisz zmiany'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * D.2 (mail 6): hard delete of an assignment added by mistake. End of
 * participation is a different path — the edit dialog's activity flag. The
 * contact card itself is never deleted, so the person stays available for
 * other trials.
 */
function DeleteAssignmentDialog({
  trialId,
  assignment,
  person,
  role,
  onClose,
  onDeleted,
}: {
  trialId: number;
  assignment: TrialPersonnelDto;
  person: string;
  role: string;
  onClose: () => void;
  onDeleted: () => void;
}): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = async (): Promise<void> => {
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      await trialsApi.deleteTrialPersonnel(trialId, assignment.id);
      onDeleted();
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się usunąć osoby z zespołu badania.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-assignment-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <h2 id="delete-assignment-title" className="text-base font-semibold text-slate-100">
          Czy na pewno usunąć z zespołu badania osobę „{person}” (rola: {role})?
        </h2>
        <p className="mt-2 text-xs text-slate-400">
          Przypisanie zostanie trwale usunięte. Karta kontaktowa osoby pozostaje w rejestrze — jeśli
          osoba kończy udział w badaniu, zamiast usuwania oznacz ją jako nieaktywną w oknie edycji.
        </p>

        {errorMessage && (
          <div
            role="alert"
            className="mt-4 flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2 border-t border-slate-800 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={() => {
              void handleDelete();
            }}
            disabled={isSubmitting}
            className="rounded-md bg-rose-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Usuwanie…' : 'Usuń z zespołu'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * D.2 (mail 6): end of participation — set active=false with an optional end
 * date. A mistake is the other path (hard delete above). The assignment stays
 * in the team history; the person stays hidden in the assign picker until
 * the "Pokaż nieaktywne" toggle. Reactivation remains edit-dialog-only.
 */
function DeactivateAssignmentDialog({
  trialId,
  assignment,
  person,
  role,
  onClose,
  onDeactivated,
}: {
  trialId: number;
  assignment: TrialPersonnelDto;
  person: string;
  role: string;
  onClose: () => void;
  onDeactivated: () => void;
}): JSX.Element {
  const [dateEnd, setDateEnd] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDeactivate = async (): Promise<void> => {
    const dateMessage = dateRangeMessage(assignment.dateStart ?? '', dateEnd);
    if (dateMessage) {
      setErrorMessage(dateMessage);
      return;
    }
    const request: UpdateTrialPersonnelRequest = { active: false, dateEnd: dateEnd.length > 0 ? dateEnd : null };
    const parsed = UpdateTrialPersonnelRequestSchema.safeParse(request);
    if (!parsed.success) {
      const dateIssue = parsed.error.issues.find((issue) => issue.path[0] === 'dateEnd');
      setErrorMessage(dateIssue?.message ?? 'Nie udało się zapisać przypisania.');
      return;
    }
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      await trialsApi.updateTrialPersonnel(trialId, assignment.id, parsed.data);
      onDeactivated();
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się oznaczyć osoby jako nieaktywnej.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="deactivate-assignment-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <h2 id="deactivate-assignment-title" className="text-base font-semibold text-slate-100">
          Oznaczyć osobę „{person}” (rola: {role}) jako nieaktywną?
        </h2>
        <p className="mt-2 text-xs text-slate-400">
          Osoba zakończy pracę w tym badaniu. Przypisanie pozostanie w historii zespołu z widocznym
          oznaczeniem, a osoba zniknie z listy wyboru przy przypisywaniu. Karta kontaktowa pozostaje
          w rejestrze bez zmian.
        </p>

        <div className="mt-4">
          <label htmlFor="deactivate-date-end" className={labelClass}>Data zakończenia (opcjonalna)</label>
          <input
            id="deactivate-date-end"
            type="date"
            disabled={isSubmitting}
            value={dateEnd}
            onChange={(event) => {
              setDateEnd(event.target.value);
            }}
            className={`${fieldClass} scheme-dark cursor-pointer`}
            onClick={openDatePicker}
            onKeyDown={blockDateTyping}
          />
        </div>

        {errorMessage && (
          <div
            role="alert"
            className="mt-4 flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2 border-t border-slate-800 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={() => {
              void handleDeactivate();
            }}
            disabled={isSubmitting}
            className="rounded-md bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-50"
          >
            {isSubmitting ? 'Oznaczanie…' : 'Oznacz jako nieaktywną'}
          </button>
        </div>
      </div>
    </div>
  );
}
